/** @module Components */

import React, { JSX } from 'react'
import { HeaderGroup, flexRender } from '@tanstack/react-table'
import tw from 'tailwind-styled-components'
import {
  ArrowSmallDownIcon,
  ArrowSmallUpIcon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline'

/** @category Prop Types */
export type HeaderProps = {
  headerGroups: HeaderGroup<Record<string, unknown>>[]
  headerRowClassName?: string
  headerCellClassName?: string
}

const StyledHeaderRow = tw.tr`
  w-full
  border-b
  border-gray-200
`

const StyledHeaderCell = tw.th`
  py-3
  text-left
  text-sm
  font-bold
  tracking-wider
`

export const CustomTableHeader: React.FC<HeaderProps> = ({
  headerGroups,
  headerRowClassName,
  headerCellClassName = ''
}: HeaderProps): JSX.Element => {
  return (
    <thead>
      {headerGroups.map((headerGroup) => (
        <StyledHeaderRow key={headerGroup.id} className={headerRowClassName}>
          {headerGroup.headers.map((header) => {
            return (
              <StyledHeaderCell
                key={header.id}
                colSpan={header.colSpan}
                className={`${headerCellClassName} ${
                  header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                }`}
                data-testid={
                  header.column.getCanSort()
                    ? 'sortable-column-header'
                    : 'column-header'
                }
                onClick={
                  header.column.getCanSort()
                    ? header.column.getToggleSortingHandler()
                    : undefined
                }
              >
                <div className="flex items-center">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                  {header.column.getCanSort() && (
                    <div className="ml-2">
                      {header.column.getIsSorted() === 'asc' ? (
                        <ArrowSmallDownIcon
                          className="h-5 w-5 text-white"
                          data-testid="sort-down-icon"
                        />
                      ) : header.column.getIsSorted() === 'desc' ? (
                        <ArrowSmallUpIcon
                          className="h-5 w-5 text-white"
                          data-testid="sort-up-icon"
                        />
                      ) : (
                        <ArrowsUpDownIcon
                          className="h-5 w-5 text-white opacity-50"
                          data-testid="sort-both-icon"
                        />
                      )}
                    </div>
                  )}
                </div>
              </StyledHeaderCell>
            )
          })}
        </StyledHeaderRow>
      ))}
    </thead>
  )
}
